<script lang="ts" module>
  // Item is one series in a readout: its name, its value at the guide, and
  // the mark it draws.
  export interface Item {
    label: string;
    value?: string;
    color: string;
    mark?: 'line' | 'dash' | 'dot' | 'faint' | 'bar' | 'hollow' | 'band';
  }
</script>

<script lang="ts">
  import type { Snippet } from 'svelte';

  // Readout is the line above a chart (spec 010 FR-033): where the guide
  // is, such as a date or a distance, and each value there, each beside the
  // mark its series draws, so it is also the chart's legend and never leaves
  // a series to its color alone. With nothing inspected, title says what the
  // values are, such as today's or the averages, or with idle how to read
  // the chart. action holds the button that opens what a selected bar or
  // day stands for. The title's row keeps its height either way, so the
  // chart under the pointer does not move when the readout fills.
  let { title, items, hint, idle = false, action }: { title: string; items: Item[]; hint?: string; idle?: boolean; action?: Snippet } = $props();
</script>

<div class="space-y-1" aria-live="polite">
  <div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 min-h-7">
    <p class={idle ? 'text-xs text-text-muted' : 'text-sm font-medium'}>{title}</p>
    {@render action?.()}
  </div>
  {#if items.length}<div class="flex flex-wrap gap-x-5 gap-y-1">
    {#each items as it (it.label)}
      <p class="flex items-center gap-1.5">
        <svg width="16" height="10" class="shrink-0" aria-hidden="true">
          {#if it.mark === 'dash'}
            <line x1="0" x2="16" y1="5" y2="5" stroke={it.color} stroke-width="2" stroke-dasharray="4 3" />
          {:else if it.mark === 'dot'}
            <circle cx="8" cy="5" r="4" fill={it.color} />
          {:else if it.mark === 'faint'}
            <circle cx="4" cy="5" r="2" fill={it.color} opacity="0.45" /><circle cx="12" cy="5" r="2" fill={it.color} opacity="0.45" />
          {:else if it.mark === 'bar'}
            <rect x="3" y="0" width="10" height="10" rx="2" fill={it.color} />
          {:else if it.mark === 'hollow'}
            <rect x="3.75" y="0.75" width="8.5" height="8.5" rx="2" fill="none" stroke={it.color} stroke-width="1.5" />
          {:else if it.mark === 'band'}
            <rect x="0" y="0" width="16" height="10" rx="2" fill={it.color} opacity="0.25" />
          {:else}
            <line x1="0" x2="16" y1="5" y2="5" stroke={it.color} stroke-width="2.5" stroke-linecap="round" />
          {/if}
        </svg>
        <span class="text-xs text-text-muted">{it.label}</span>
        {#if it.value}<span class="text-sm font-semibold tabular-nums">{it.value}</span>{/if}
      </p>
    {/each}
  </div>{/if}
  {#if hint}<p class="text-xs text-text-muted">{hint}</p>{/if}
</div>
